interface CharCounterProps {
  /** Current value length (typically `value.length`). */
  length: number
  /** Hard upper bound — when reached, browser stops accepting input. */
  maxLength: number
  /**
   * Render for a flex row rather than as a standalone block: drops the
   * top margin and the `text-right` (the row's `justify-between` does the
   * alignment) and adds `shrink-0` so a long helper text can't squeeze
   * the counter onto two lines. Used when the field pairs the counter
   * with helper text on one row.
   */
  inline?: boolean
  /** Optional override className for layout (e.g., margin tweaks). */
  className?: string
}

/**
 * Shared character counter for Input + TextArea. Renders `{length}/{max}`
 * with progressive color thresholds:
 *   - <80%   muted (subtle, doesn't compete with the field)
 *   - >=80%  amber (heads-up — operator is approaching the limit)
 *   - >=100% destructive red (browser will stop accepting more input)
 *
 * Right-aligned by default to match standard composer counter UX
 * (Twitter, Slack, GitHub PR description, etc.).
 */
export function CharCounter({ length, maxLength, inline = false, className = '' }: CharCounterProps) {
  if (maxLength <= 0) return null
  const ratio = length / maxLength
  let colorClass = 'text-muted-foreground'
  if (ratio >= 1) {
    colorClass = 'text-destructive'
  } else if (ratio >= 0.8) {
    colorClass = 'text-amber-600 dark:text-amber-400'
  }
  const layoutClass = inline ? 'shrink-0 text-xs' : 'mt-1 text-xs text-right'
  return (
    <p
      // aria-live=polite so a screen reader announces "475 of 512" when
      // the operator approaches the limit, without interrupting their
      // typing.
      aria-live="polite"
      className={`${layoutClass} ${colorClass} ${className}`.trim()}
    >
      {length}/{maxLength}
    </p>
  )
}
